import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Menu, Phone, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Logo } from '@/components/generals/logo'
import { services } from '@/constants/services'
import { siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const toggleRef = useRef<HTMLButtonElement>(null)
    const [open, setOpen] = useState(false)

    const path = router.asPath.split(/[?#]/)[0]
    const isActive = (href: string): boolean => path === href || path.startsWith(`${href}/`)

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setOpen(false)
            toggleRef.current?.focus()
        }

        document.addEventListener('keydown', onKeyDown)

        return () => document.removeEventListener('keydown', onKeyDown)
    }, [open])

    return (
        <header className='sticky top-0 z-40 border-b border-foreground/10 bg-background/90 backdrop-blur-md'>
            <div className='shell flex h-16 items-center justify-between gap-4 lg:h-[4.5rem]'>
                <Logo />

                <nav aria-label='Main' className='hidden lg:block'>
                    <ul className='flex items-center'>
                        {services.map((service) => {
                            const href = `/${service.slug}`
                            const active = isActive(href)

                            return (
                                <li key={service.slug}>
                                    <Link
                                        href={href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative block rounded-md px-2.5 py-2 text-sm font-medium text-foreground/70 transition-colors hover:text-foreground xl:px-3.5 xl:text-[0.95rem]',
                                            active && 'text-foreground after:absolute after:inset-x-2.5 after:-bottom-[17px] after:h-0.5 after:bg-primary xl:after:inset-x-3.5'
                                        )}
                                    >
                                        {service.shortName}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-1.5'>
                    <a
                        href={siteConfig.phone.href}
                        className='hidden items-center gap-2 rounded-md px-3 py-2 text-[0.95rem] font-medium whitespace-nowrap transition-colors hover:bg-foreground/5 xl:inline-flex'
                    >
                        <Phone aria-hidden='true' className='size-4' />
                        {siteConfig.phone.display}
                    </a>

                    <a
                        href={siteConfig.phone.href}
                        aria-label={`Call ${siteConfig.phone.display}`}
                        className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'xl:hidden')}
                    >
                        <Phone aria-hidden='true' className='size-5' />
                    </a>

                    <Link
                        href='/contact'
                        className={cn(buttonVariants({ size: 'sm' }), 'hidden sm:inline-flex')}
                    >
                        Get a free quote
                    </Link>

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen((current) => !current)}
                        className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'lg:hidden')}
                    >
                        {open
                            ? <X aria-hidden='true' className='size-5' />
                            : <Menu aria-hidden='true' className='size-5' />}

                        <span className='sr-only'>
                            {open ? "Close menu" : "Open menu"}
                        </span>
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                hidden={!open}
                className='max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-foreground/10 bg-background lg:hidden'
            >
                <nav aria-label='Mobile' className='shell pt-2 pb-6'>
                    <ul>
                        {services.map((service) => {
                            const href = `/${service.slug}`

                            return (
                                <li key={service.slug} className='border-b border-foreground/10'>
                                    <Link
                                        href={href}
                                        aria-current={isActive(href) ? 'page' : undefined}
                                        className='flex items-center justify-between gap-4 py-4 text-lg font-medium aria-[current=page]:text-foreground'
                                    >
                                        <span className='flex items-baseline gap-3'>
                                            <span className='font-mono text-xs text-muted-foreground'>{service.index}</span>
                                            {service.name}
                                        </span>

                                        <ArrowRight aria-hidden='true' className='size-4 text-muted-foreground' />
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='mt-6 flex flex-col gap-3'>
                        <Link href='/contact' className={cn(buttonVariants({ size: 'lg' }), 'w-full')}>
                            Get a free quote
                        </Link>

                        <a href={siteConfig.phone.href} className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'w-full')}>
                            <Phone aria-hidden='true' />
                            Call {siteConfig.phone.display}
                        </a>
                    </div>
                </nav>
            </div>
        </header>
    )
}
